import { Box } from '@mui/system';
import { useCallback, useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  Checkbox,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  Stack,
  tableCellClasses,
  TextField,
  Typography,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Select,
  MenuItem
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { MdEmail, MdTextsms } from 'react-icons/md';
import { RiWhatsappFill } from 'react-icons/ri';
import { useDispatch } from 'react-redux';
import { format } from 'date-fns';
import BrudCrumbs from '../../components/BreadCrumbs';
import Label from '../../components/Label';
import Page from '../../components/Page';
import Scrollbar from '../../components/Scrollbar';
import SearchNotFound from '../../components/SearchNotFound';
import { UserListHead, UserMoreMenu } from '../../components/_dashboard/user';
import { fDate, fDateNew } from '../../utils/formatTime';
import BankopsToolbar from './BankopsToolbar';
import { NOT_AVAILABLE } from '../Global/Constants';
import { normalizeValue } from '../../utils/CommonFunction';
import { getBankOpsList } from '../../store/Slice/Bank/BankSlice';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import useBankSelector from '../../store/Slice/Bank/BankSelector';
import useDebounce from '../../utils/useDebounce';
import RevenueService from '../services/RevenueService';

const TABLE_HEAD = [
  { id: 'id', label: 'APPLICATION ID', alignRight: false },
  { id: 'lanId', label: 'LAN ID / FILE NO.', alignRight: false },
  { id: 'created', label: 'SUBMISSION DATE', alignRight: false },
  { id: 'name', label: 'NAME', alignRight: false },
  { id: 'companyName', label: 'COMPANY NAME', alignRight: false },
  { id: 'contactNumber', label: 'CONTACT NUMBER', alignRight: false },
  { id: 'sourceFlag', label: 'FILE SOURCE', alignRight: false },
  { id: 'loanType', label: 'PRODUCT', alignRight: false },
  { id: 'city', label: 'CITY', alignRight: false },
  { id: 'amount', label: 'AMOUNT', alignRight: false },
  { id: 'bankName', label: 'BANK', alignRight: false },
  { id: 'dsaName', label: 'DSA NAME', alignRight: false },
  { id: 'status', label: 'STATUS', alignRight: false },
  { id: 'lastUpdate', label: 'LAST UPDATE', alignRight: false },
  { id: 'action', label: 'ACTION', alignRight: false, align: 'center' }
];

const StyledTableRow = styled(TableRow)(() => ({
  backgroundColor: '#fff',
  '&:nth-of-type(even)': {
    backgroundColor: '#fafbfc'
  },
  '&:hover': {
    backgroundColor: '#f1f5f9 !important'
  },
  '&:last-child td, &:last-child th': { border: 0 }
}));

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    fontSize: 11.5,
    fontWeight: 700,
    padding: '12px 14px',
    color: '#64748b',
    textTransform: 'uppercase',
    backgroundColor: '#ffffff',
    borderBottom: '1px solid #e2e8f0',
    letterSpacing: '0.5px'
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 13,
    padding: '12px 14px',
    borderBottom: '1px solid #f1f5f9',
    color: '#334155'
  }
}));

function Bankops() {
  const [selected, setSelected] = useState([]);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState();
  const [filterName, setFilterName] = useState('');
  const [sharePopUp, setSharePopUp] = useState(false);
  const [value] = useState(1);
  const [isActive, setActive] = useState(false);
  const [convertLead, setConvertLead] = useState(false);
  const [filterState, setFilterState] = useState({
    product: ' ',
    cityId: null,
    status: ' '
  });
  const [filterDatesRange, setFilterDatesRange] = useState([
    { startDate: null, endDate: null, key: 'selection' }
  ]);
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const [hasNextPage, setHasNextPage] = useState(true);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const debouncedSearch = useDebounce(filterName, 600);

  const { LoginData } = useLoginSelector();
  const { BankListData } = useBankSelector();
  const { DownloadBankopsRecord } = RevenueService;

  useEffect(() => {
    localStorage.removeItem('selectedItem');
  }, []);

  const getBankListApi = useCallback(() => {
    const credentials = {
      companyId: LoginData?.companyId,
      userId: LoginData?.userId,
      accessLevelId: LoginData?.accessLevelId,
      stageId: 58,
      filterModel: {
        bankId: filterState.bankId || null,
        cityId: filterState.cityId || null,
        loanTypeId: filterState.product || null,
        leadStatusId: filterState.status || null,
        bOpsDecisionTypeId: filterState.bOpsDecisionTypeId || null
      },
      entityListRequestModel: {
        searchLiteral: debouncedSearch?.length >= 2 ? debouncedSearch : null,
        sortOrder: order || 'desc',
        pageSize: rowsPerPage,
        pageNumber: page + 1
      }
    };
    dispatch(getBankOpsList(credentials));
  }, [LoginData, filterState, order, page, rowsPerPage, dispatch, debouncedSearch]);

  useEffect(() => {
    getBankListApi();
  }, [getBankListApi]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, filterState, filterDatesRange, value]);

  useEffect(() => {
    setHasNextPage((BankListData?.length || 0) >= rowsPerPage);
  }, [BankListData, rowsPerPage]);

  const handleRequestSort = useCallback(
    (_, property) => {
      if (property !== 'action') {
        setOrder((prev) => (orderBy === property && prev === 'asc' ? 'desc' : 'asc'));
        setOrderBy(property);
      }
    },
    [orderBy]
  );

  const handleSelectAllClick = useCallback(
    (event) => {
      if (event.target.checked) {
        const list = (BankListData || []).filter((item) => {
          const status = item.logInStatusName?.toLowerCase();
          return status !== 'rejected' && status !== 'sanctioned';
        });
        setSelected(list);
      } else {
        setSelected([]);
      }
    },
    [BankListData]
  );

  const handleClick = useCallback((_, item) => {
    setSelected((prev) => {
      const selectedIndex = prev.indexOf(item);
      if (selectedIndex === -1) return [...prev, item];
      return prev.filter((i) => i !== item);
    });
  }, []);

  const handleDownload = async (selectedItems) => {
    const credentials = {
      userId: LoginData?.userId,
      accessLevelId: LoginData?.accessLevelId,
      selectedIds: selectedItems?.map((item) => item.applicationId) || [],
      filterModel: {
        cityId: filterState.cityId || null,
        status: filterState.status || null,
        loanTypeId: filterState.product || null
      }
    };

    try {
      const response = await DownloadBankopsRecord(credentials, LoginData?.jwt);
      const blob = await response.blob();
      const fileName = `BankopsDataDownload_${format(new Date(), 'dd-MM-yyyy_HH-mm-ss')}.xlsx`;
      const url = window.URL.createObjectURL(
        new Blob([blob], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        })
      );
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export error:', error);
    }
  };

  return (
    <Page title="Partner Management System || Application">
      <Container maxWidth="xl" sx={{ pb: 6, pt: 2 }}>
        {/* Top Header Row with Icon, Title, and Breadcrumb */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          sx={{ mb: 3.5, gap: 2 }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 46,
                height: 46,
                borderRadius: '12px',
                bgcolor: '#e0f2fe',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#0284c7'
              }}
            >
              <AccountBalanceOutlinedIcon sx={{ fontSize: 26 }} />
            </Box>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: '#0f172a',
                  fontSize: { xs: '20px', sm: '24px' },
                  letterSpacing: '-0.3px',
                  mb: 0.25
                }}
              >
                Application
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b', fontSize: '13px' }}>
                Manage and process bank applications and loan source logs.
              </Typography>
            </Box>
          </Stack>

          <Box>
            <BrudCrumbs />
          </Box>
        </Stack>

        <Card
          sx={{
            borderRadius: '16px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
            overflow: 'hidden',
            bgcolor: '#ffffff'
          }}
        >
          <BankopsToolbar
            numSelected={selected.length}
            filterName={filterName}
            setFilterName={setFilterName}
            onFilterName={(e) => setFilterName(e.target.value)}
            filterDatesRange={filterDatesRange}
            setFilterDatesRange={setFilterDatesRange}
            filterState={filterState}
            setFilterState={setFilterState}
            parent="bankops"
            selected={selected}
            handleDownload={handleDownload}
          />
          <Scrollbar>
            <TableContainer sx={{ minWidth: 800, minHeight: 380, overflow: 'auto' }}>
              <Table>
                <UserListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={BankListData?.length}
                  numSelected={selected.length}
                  onRequestSort={handleRequestSort}
                  onSelectAllClick={handleSelectAllClick}
                  isCheckBox="true"
                />
                <TableBody>
                  {BankListData?.map((item) => {
                    const isItemSelected = selected.includes(item);
                    const statusLower = item.logInStatusName?.toLowerCase();
                    const isDisabled = statusLower === 'rejected' || statusLower === 'sanctioned';

                    return (
                      <StyledTableRow
                        hover
                        key={item.applicationId}
                        tabIndex={-1}
                        role="checkbox"
                        selected={isItemSelected}
                        aria-checked={isItemSelected}
                      >
                        <TableCell padding="checkbox" sx={{ pl: 2.5 }}>
                          <Checkbox
                            className={isDisabled ? 'disabledDocument' : 'enabledDocument'}
                            checked={isItemSelected}
                            onChange={(e) => handleClick(e, item)}
                            sx={{
                              color: '#cbd5e1',
                              '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: '#0284c7' }
                            }}
                          />
                        </TableCell>
                        <StyledTableCell align="left">
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: 600,
                              color: '#0284c7',
                              fontSize: '13px'
                            }}
                          >
                            {normalizeValue(item?.applicationId, NOT_AVAILABLE)}
                          </Typography>
                        </StyledTableCell>
                        <StyledTableCell align="left" sx={{ color: '#64748b' }}>
                          {normalizeValue(item?.lanId, NOT_AVAILABLE)}
                        </StyledTableCell>
                        <StyledTableCell align="left" sx={{ color: '#64748b' }}>
                          {item?.submissionDate ? fDate(item.submissionDate) : NOT_AVAILABLE}
                        </StyledTableCell>
                        <StyledTableCell align="left" sx={{ fontWeight: 600, color: '#0f172a' }}>
                          {normalizeValue(item?.leadName, NOT_AVAILABLE)}
                        </StyledTableCell>
                        <StyledTableCell align="left" sx={{ color: '#64748b' }}>
                          {normalizeValue(item?.companyName, NOT_AVAILABLE)}
                        </StyledTableCell>
                        <StyledTableCell align="left" sx={{ color: '#64748b' }}>
                          {normalizeValue(item?.contactNumber, NOT_AVAILABLE)}
                        </StyledTableCell>
                        <StyledTableCell align="left">
                          <Label
                            variant="ghost"
                            color={
                              item?.sourceFlag === 'LOGIN_FILE' ||
                              item?.logInStatusName === 'Rejected'
                                ? 'warning'
                                : 'primary'
                            }
                            sx={{ opacity: 0.8, fontWeight: 500 }}
                          >
                            {normalizeValue(item?.sourceFlag, NOT_AVAILABLE)}
                          </Label>
                        </StyledTableCell>
                        <StyledTableCell align="left" sx={{ color: '#64748b' }}>
                          {normalizeValue(item?.loanTypeName, NOT_AVAILABLE)}
                        </StyledTableCell>
                        <StyledTableCell align="left" sx={{ color: '#64748b' }}>
                          {normalizeValue(item?.cityName, NOT_AVAILABLE)}
                        </StyledTableCell>
                        <StyledTableCell align="left" sx={{ color: '#0f172a', fontWeight: 600 }}>
                          {normalizeValue(item?.loanAmount, NOT_AVAILABLE)}
                        </StyledTableCell>
                        <StyledTableCell align="left" sx={{ color: '#64748b' }}>
                          {normalizeValue(item?.bankPrefName, NOT_AVAILABLE)}
                        </StyledTableCell>
                        <StyledTableCell align="left" sx={{ color: '#64748b' }}>
                          {normalizeValue(item?.dsaName, NOT_AVAILABLE)}
                        </StyledTableCell>
                        <StyledTableCell align="left">
                          <Label
                            variant="ghost"
                            color={
                              ['u-login', 'sanctioned'].includes(statusLower)
                                ? 'warning'
                                : 'success'
                            }
                          >
                            {normalizeValue(
                              item?.logInStatusName?.replace(' ', '\n'),
                              NOT_AVAILABLE
                            )}
                          </Label>
                        </StyledTableCell>
                        <StyledTableCell align="left" sx={{ color: '#64748b' }}>
                          {normalizeValue(fDateNew(item?.lastUpdatedOn), NOT_AVAILABLE, fDate)}
                        </StyledTableCell>
                        <StyledTableCell align="center">
                          <UserMoreMenu rowId={item} parent="bankops" />
                        </StyledTableCell>
                      </StyledTableRow>
                    );
                  })}
                </TableBody>
                {BankListData?.length === 0 && (
                  <TableBody>
                    <TableRow>
                      <StyledTableCell align="center" colSpan={15} sx={{ py: 3 }}>
                        <SearchNotFound searchQuery={filterName} />
                      </StyledTableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>

          {/* Modern Pagination Footer */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems="center"
            justifyContent="space-between"
            sx={{ p: 2, px: 3, borderTop: '1px solid #f1f5f9', bgcolor: '#ffffff', gap: 2 }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Typography sx={{ fontSize: '13px', color: '#64748b' }}>Showing</Typography>
              <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#0f172a' }}>
                {BankListData?.length || 0} records
              </Typography>
            </Stack>

            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography sx={{ fontSize: '13px', color: '#64748b' }}>
                Page <span style={{ fontWeight: 600, color: '#0f172a' }}>{page + 1}</span>
              </Typography>

              <Stack direction="row" spacing={1}>
                <IconButton
                  disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  sx={{
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    p: 0.75,
                    bgcolor: '#ffffff',
                    color: '#0f172a',
                    '&.Mui-disabled': { opacity: 0.4, bgcolor: '#f8fafc' }
                  }}
                >
                  <ChevronLeftIcon sx={{ fontSize: 18 }} />
                </IconButton>
                <IconButton
                  disabled={!hasNextPage}
                  onClick={() => setPage((p) => p + 1)}
                  sx={{
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    p: 0.75,
                    bgcolor: '#ffffff',
                    color: '#0f172a',
                    '&.Mui-disabled': { opacity: 0.4, bgcolor: '#f8fafc' }
                  }}
                >
                  <ChevronRightIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Stack>
            </Stack>
          </Stack>
        </Card>

        <Dialog
          open={sharePopUp}
          onClose={() => setSharePopUp(false)}
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: '16px',
              overflow: 'hidden'
            }
          }}
        >
          <DialogTitle className="popupTitle">
            <Typography variant="h6">Select Sharing Option</Typography>
            <IconButton onClick={() => setSharePopUp(false)} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Box className="displayFlex alignItemCenter justifyContentCenter" mb={2}>
              {['whatsapp', 'email', 'sms'].map((type) => (
                <Box
                  key={type}
                  className={`mailSMSType mr-2 ${isActive === type ? 'active' : ''}`}
                  onClick={() => setActive(type)}
                >
                  {type === 'whatsapp' && <RiWhatsappFill color="#2CB742" size={48} />}
                  {type === 'email' && <MdEmail color="#0d4689" size={48} />}
                  {type === 'sms' && <MdTextsms color="#4fbbc8" size={48} />}
                </Box>
              ))}
            </Box>
            <FormControl required fullWidth className="formGroup">
              <Typography variant="body1" className="formLabel">
                Email/Mobile
              </Typography>
              <TextField
                fullWidth
                inputProps={{ className: 'textInput' }}
                placeholder="Enter Email/Mobile"
              />
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSharePopUp(false)} variant="contained" className="mainBtn">
              Send
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={convertLead}
          onClose={() => setConvertLead(false)}
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: '16px',
              overflow: 'hidden'
            }
          }}
        >
          <DialogContent dividers>
            <Box py={2}>Your Recommendation has been successfully converted to leads.</Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConvertLead(false)} variant="contained" className="mainBtn">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Page>
  );
}

export default Bankops;
